import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Divider, PaperProps } from '@material-ui/core';
import cx from 'classnames';
import { get, kebabCase } from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { translate } from '../../../../../common/intl';
import { LINE_TYPES } from '../../../../../state/ducks/attachments/constants';
import { AttachmentType } from '../../../../../state/ducks/attachments/types';
import { companySelectors } from '../../../../../state/ducks/company';
import { DocumentRevision, DocumentRevisionStatus } from '../../../../../state/ducks/documentRevisions/types';
import { Attachment } from '../../../../change.request/form/types';
import { attachmentsDisabledChangeControl } from '../../../../document.revision/utils/helpers';
import { FormHeaderContext, isOwnerContext } from '../../../../documentRevision/forms/DocumentRevisionForm.container';
import { isDocumentRevisionInStatus, isDocumentRevisionPrevious, isDocumentRevisionStatusWithDraftWatermark } from '../../../../documentRevision/helpers/documentRevisionStatus';
import { CellTemplateProps } from '../../../KendoDataGrid/KendoDataGrid.types';
import Text from '../../../Text';
import MenuItemSetPrimary from '../../attachment.icon/menu.setPrimary';
import HeaderMenu from '../../header/Menu';
import { PREVIOUS_REVISION_PDF_FILE_TYPES, WATERMARK_FILES } from '../../view.file/utils/constants';
import { checkIfFileIsViewable } from '../../view.file/utils/helpers';
import { useStyles } from '../styles';

interface Props extends CellTemplateProps<Attachment> {
  documentRevision?: DocumentRevision
  documentRevisionId?: string
  documentRevisionStatus?: DocumentRevisionStatus
  onDownloadAttachment?: (attachment: Attachment, type?: AttachmentType) => void
  onDownloadAsPDF?: (attachment: Attachment, attachmentType: AttachmentType, documentRevisionId?: string) => void
  onView?: (attachment: Attachment) => (evt: React.MouseEvent<HTMLElement>) => void
  onDelete?: (attachment: Attachment) => () => void
}

const ActionNeededCellTemplate: React.FC<Props> = (props) => {
  const { dataItem: attachment, documentRevision, documentRevisionId, documentRevisionStatus, onDownloadAttachment, onDownloadAsPDF, onView, onDelete, dataIndex } = props;
  const classes = useStyles();
  const type = get(attachment, 'type') as AttachmentType;
  const isFileViewable = useMemo(() => checkIfFileIsViewable(type), [type]);
  const revisionStatus = documentRevisionStatus ?? documentRevision?.status ?? DocumentRevisionStatus.Released;
  const isPreviousRevision = isDocumentRevisionPrevious(revisionStatus);
  const restrictPreviousRevisionDownloads = useSelector(companySelectors.isPreviousRevisionAttachmentDownloadRestrictionEnabled);
  const isUnreleased = isDocumentRevisionStatusWithDraftWatermark(revisionStatus);
  const watermarkRevisionId = isPreviousRevision || isUnreleased
    ? documentRevisionId ?? documentRevision?.id
    : undefined;
  const supportsPreviousRevisionPDF = PREVIOUS_REVISION_PDF_FILE_TYPES.includes(String(type).toLowerCase());

  const isDisabledInRestrictedEdit = attachmentsDisabledChangeControl(documentRevision);

  const requiredStatus = isDocumentRevisionInStatus(
    [DocumentRevisionStatus.Draft, DocumentRevisionStatus.PendingChange],
    documentRevision?.status,
  );
  const canSetAsPrimary = requiredStatus && documentRevision?.document?.canAccess
    && attachment.lineType !== LINE_TYPES.RED_LINE && !isDisabledInRestrictedEdit;
  const isDocumentPage = Boolean(React.useContext(FormHeaderContext));
  const isNewDocument = isDocumentPage && !documentRevision?.id;
  const showSetAsPrimary = attachment.id !== documentRevision?.primaryAttachment?.id && canSetAsPrimary && !isNewDocument && isDocumentPage;
  const isUserDocumentOwner = React.useContext(isOwnerContext);
  const canWatermark = useMemo(() => WATERMARK_FILES.includes(type), [type]);

  if (isPreviousRevision && restrictPreviousRevisionDownloads && !supportsPreviousRevisionPDF) {
    return null;
  }

  const renderDownloadAsPDF = (
    <div
      className={classes.columnMenuAction}
      onClick={() => onDownloadAsPDF?.(attachment, AttachmentType.Attached, watermarkRevisionId)}
      data-cy="option-download-as-pdf"
    >
      <FontAwesomeIcon icon={regular('file-lines')} className={classes.menuIcons} />
      <Text message={translate('attachment.menu.download.as.pdf')} />
    </div>
  );

  return (
    <HeaderMenu
      columnMenuItemClasses={classes.columnMenuItem}
      PaperProps={{ 'data-cy': `attachment-popover-${dataIndex}` } as PaperProps}
      menuIconButtonClass={classes.iconButton}
      dataCy={`${kebabCase(attachment?.name)}-menu-${dataIndex}`}
      icon={<FontAwesomeIcon className={cx('k-icon', classes.icon)} icon={solid('ellipsis-vertical')} />}
    >
      {isPreviousRevision && restrictPreviousRevisionDownloads ? renderDownloadAsPDF : (
        <>
          <div className={classes.columnMenuAction} onClick={() => onDownloadAttachment?.(attachment)} data-cy="option-download">
            <FontAwesomeIcon icon={regular('arrow-down-to-line')} className={classes.menuIcons} />
            <Text message={translate('attachment.menu.download.file')} />
          </div>
          {canWatermark && (
            <div className={classes.columnMenuAction} onClick={() => onDownloadAsPDF?.(attachment, AttachmentType.Attached, watermarkRevisionId)} data-cy="option-download-as-pdf">
              <FontAwesomeIcon icon={regular('file-lines')} className={classes.menuIcons} />
              <Text message={translate('attachment.menu.download.as.pdf')} />
            </div>
          )}
          {isFileViewable && (
            <div className={classes.columnMenuAction} onClick={onView?.(attachment)} data-cy="option-view">
              <FontAwesomeIcon icon={regular('eye')} className={classes.menuIcons} />
              <Text message={translate('attachment.menu.view')} />
            </div>
          )}
          {isUserDocumentOwner && requiredStatus && !isDisabledInRestrictedEdit && (
            <div className={classes.columnMenuAction} onClick={onDelete?.(attachment)} data-cy="option-delete">
              <FontAwesomeIcon icon={regular('trash-can')} className={cx(classes.menuIcons, classes.menuDeleteIcon)} />
              <Text message={translate('common.delete')} />
            </div>
          )}
          {showSetAsPrimary && !isNewDocument && (
            <div data-cy="option-set-as-primary" className={classes.columnMenuItemPrimary} >
              <Divider />
              <MenuItemSetPrimary
                attachment={attachment}
                documentRevision={documentRevision}
                menuItemClassName={classes.columnMenuItemPrimaryAction}
              />
            </div>
          )}
        </>
      )}
    </HeaderMenu>
  );
};

export default ActionNeededCellTemplate;
